/**
 * Client-side version drift detector.
 *
 * The server stamps every response with `X-App-Version` and `X-App-Build`.
 * When this tab is running a bundle the server has already replaced (the
 * iPad-Safari-cached SPA shell problem we've been chasing), force a one-time
 * hard reload so the user lands on the current build. Staleness is decided by
 * the build SHA, not the version string -- see checkVersionFromResponse for
 * why. Guarded against an infinite loop: at most one auto-reload per tab per
 * server build; a mismatch that persists after that surfaces via a callback
 * instead.
 */

import { saveReturnLocation } from './authSession';
import { changelog } from './changelog';
import { BUILD_SHORT_SHA } from './buildInfo';

const CLIENT_VERSION = changelog[0]?.version ?? 'v0.0.0';
const CLIENT_BUILD = BUILD_SHORT_SHA;
const RELOAD_ATTEMPTED_KEY = 'irrig_version_reload_attempted';

/** -1 if a < b, 0 if equal, 1 if a > b. Tolerates `v` prefix and short forms. */
function compareVersions(a: string, b: string): number {
  const parse = (s: string) => s.replace(/^v/, '').split('.').map((p) => parseInt(p, 10) || 0);
  const [a0 = 0, a1 = 0, a2 = 0] = parse(a);
  const [b0 = 0, b1 = 0, b2 = 0] = parse(b);
  if (a0 !== b0) return a0 < b0 ? -1 : 1;
  if (a1 !== b1) return a1 < b1 ? -1 : 1;
  if (a2 !== b2) return a2 < b2 ? -1 : 1;
  return 0;
}

let warnedThisSession = false;
let onMismatchAfterReload: ((info: { client: string; server: string }) => void) | null = null;

/** Optional: app can register a handler to show a "please refresh" banner if
 *  the version mismatch persists *after* the one-time auto-reload. */
export function onPersistentVersionMismatch(
  cb: (info: { client: string; server: string }) => void,
): void {
  onMismatchAfterReload = cb;
}

/**
 * Read a response's X-App-Build / X-App-Version headers and decide whether this
 * tab's bundle is stale. Called from apiClient on every successful response.
 *
 * Behavior:
 *   - same build SHA: clear the reload flag if set (a prior reload worked).
 *   - different build SHA, no prior reload for it: set flag, force-reload with
 *     a cache-busting query string (iPad Safari sometimes ignores plain
 *     location.reload()), telemetry breadcrumb on the way out.
 *   - different build SHA, reload already attempted for it: don't loop; surface
 *     via onPersistentVersionMismatch if registered. Warn to console once.
 *   - either side reports build 'local' (dev): fall back to comparing semver,
 *     and do nothing unless the client is genuinely behind.
 *   - no X-App-Version header at all: do nothing (older server build).
 */
export function checkVersionFromResponse(headers: Headers): void {
  const serverVersion = headers.get('X-App-Version');
  if (!serverVersion) return;
  const serverBuild = headers.get('X-App-Build') ?? '';
  // Which signal decides "stale"? The build SHA, whenever both sides know it.
  // The two version strings come from sources that have legitimately drifted:
  // the client's is `changelog[0].version` (bumped on every user-facing change)
  // while the server's is `server/package.json` (rarely touched). The client
  // therefore reads as *ahead* on every deploy, so a semver comparison can
  // never detect staleness -- it just hits the "client ahead, ignore" branch.
  // The build SHA has no such problem: the same App Service instance serves
  // both this bundle and the API, so a differing SHA means this tab is running
  // code the server has already replaced. Semver stays only as the fallback
  // for a server too old to send X-App-Build.
  const buildsComparable =
    !!serverBuild && !!CLIENT_BUILD && serverBuild !== 'local' && CLIENT_BUILD !== 'local';
  const isStale = buildsComparable
    ? serverBuild !== CLIENT_BUILD
    : compareVersions(CLIENT_VERSION, serverVersion) < 0;

  if (!isStale) {
    // In sync -- if a previous reload got us here, it did its job; drop the marker.
    try {
      if (sessionStorage.getItem(RELOAD_ATTEMPTED_KEY)) {
        sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY);
      }
    } catch {
      /* sessionStorage unavailable (Safari private mode) -- nothing to clear */
    }
    return;
  }

  // This tab is stale. Reload once per tab per (server-version + server-build);
  // a later distinct push gets its own attempt rather than being suppressed
  // as "we already tried."
  const currentReloadKey = `${serverVersion}/${serverBuild}`;
  let alreadyAttempted = false;
  try {
    alreadyAttempted = sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === currentReloadKey;
  } catch {
    /* sessionStorage unavailable -- behave as if attempted so we don't loop */
    alreadyAttempted = true;
  }

  if (alreadyAttempted) {
    if (!warnedThisSession) {
      warnedThisSession = true;
      console.warn(
        `[versionCheck] persistent mismatch after reload: client=${CLIENT_VERSION} server=${serverVersion}`,
      );
      // Fire a telemetry breadcrumb so the maintainer sees this in event_logs.
      void import('./telemetry').then(({ logEvent }) =>
        logEvent(
          'client_version_mismatch_unresolved',
          `client=${CLIENT_VERSION}/${CLIENT_BUILD} server=${serverVersion}/${serverBuild}`,
          {
            severity: 'warning',
            metadata: {
              client_version: CLIENT_VERSION,
              client_build: CLIENT_BUILD,
              server_version: serverVersion,
              server_build: serverBuild,
            },
          },
        ),
      ).catch(() => {});
      if (onMismatchAfterReload) {
        try {
          onMismatchAfterReload({ client: CLIENT_VERSION, server: serverVersion });
        } catch {
          /* never let UI callback take down the request flow */
        }
      }
    }
    return;
  }

  // First-time mismatch: record intent, breadcrumb, then reload.
  try {
    sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, currentReloadKey);
  } catch {
    /* if we can't record, abort the reload — we'd loop forever */
    return;
  }
  void import('./telemetry').then(({ logEvent }) =>
    logEvent(
      'client_version_mismatch_reload',
      `client=${CLIENT_VERSION}/${CLIENT_BUILD} server=${serverVersion}/${serverBuild}`,
      {
        severity: 'info',
        metadata: {
          client_version: CLIENT_VERSION,
          client_build: CLIENT_BUILD,
          server_version: serverVersion,
          server_build: serverBuild,
        },
      },
    ),
  ).catch(() => {});
  // Drop a return breadcrumb first. This reload is involuntary and can land in
  // the middle of an inspection; without it the tech comes back on the landing
  // screen (the app routes by view state, not by URL) and has to find their
  // report again. Same mechanism the re-auth path uses, so the restore is
  // already wired up on the other side.
  saveReturnLocation();
  // Cache-buster query string -- iPad Safari has been observed to serve an
  // old index.html out of its disk cache on plain location.reload().
  const url = new URL(window.location.href);
  url.searchParams.set('_v', String(Date.now()));
  window.location.replace(url.toString());
}
